import axios, { type AxiosInstance, type AxiosResponse } from 'axios'
import { z } from 'zod'

// API Response schemas
export const ApiResponseSchema = z.object({
  success: z.boolean(),
  data: z.any().optional(),
  error: z.string().optional(),
})

export const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(6),
})

export const RegisterSchema = z.object({
  email: z.string().email(),
  password: z.string().min(6),
  name: z.string().min(1),
})

export const UserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  role: z.string(),
  isActive: z.boolean(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
})

export const CreateUserSchema = z.object({
  email: z.string().email(),
  name: z.string().optional(),
  role: z.string().optional(),
})

export const ColorPaletteRequestSchema = z.object({
  image: z.string(), // Base64 encoded image or image URL
  colorCount: z.number().int().min(2).max(20).default(8),
  quality: z.number().int().min(1).max(10).default(10),
  includeHex: z.boolean().default(true),
  includeRgb: z.boolean().default(true),
  includeHsl: z.boolean().default(false),
})

export const ColorInfoSchema = z.object({
  hex: z.string().optional(),
  rgb: z.object({
    r: z.number().int().min(0).max(255),
    g: z.number().int().min(0).max(255),
    b: z.number().int().min(0).max(255),
  }).optional(),
  hsl: z.object({
    h: z.number().min(0).max(360),
    s: z.number().min(0).max(100),
    l: z.number().min(0).max(100),
  }).optional(),
  percentage: z.number().min(0).max(100).optional(),
})

export const ColorPaletteResponseSchema = z.object({
  colors: z.array(ColorInfoSchema),
  dominantColor: ColorInfoSchema,
  palette: z.array(ColorInfoSchema),
  imageInfo: z.object({
    width: z.number().optional(),
    height: z.number().optional(),
    format: z.string().optional(),
  }).optional(),
  processingTime: z.number(),
  algorithm: z.string(),
})

export type ApiResponse<T = any> = z.infer<typeof ApiResponseSchema> & { data?: T }
export type LoginRequest = z.infer<typeof LoginSchema>
export type RegisterRequest = z.infer<typeof RegisterSchema>
export type User = z.infer<typeof UserSchema>
export type CreateUserRequest = z.infer<typeof CreateUserSchema>
export type ColorPaletteRequest = z.infer<typeof ColorPaletteRequestSchema>
export type ColorInfo = z.infer<typeof ColorInfoSchema>
export type ColorPaletteResponse = z.infer<typeof ColorPaletteResponseSchema>

/** Opt-in API tracing: in dev tools run `localStorage.setItem('DEBUG_API', '1'); location.reload()` */
function isApiDebug(): boolean {
  if (typeof localStorage === 'undefined') return false
  if (!import.meta.env.DEV) return false
  return localStorage.getItem('DEBUG_API') === '1'
}

const API_REQUEST_LOG_MAX = 120
/** Recent requests (dev + DEBUG_API only); inspect in console: `window.__apiRequestLog` */
type ApiRequestLogEntry = { t: number; method: string; url: string }
function pushApiRequestLog(entry: ApiRequestLogEntry): void {
  if (typeof window === 'undefined' || !isApiDebug()) return
  const w = window as Window & { __apiRequestLog?: ApiRequestLogEntry[] }
  if (!w.__apiRequestLog) w.__apiRequestLog = []
  w.__apiRequestLog.push(entry)
  if (w.__apiRequestLog.length > API_REQUEST_LOG_MAX) {
    w.__apiRequestLog.splice(0, w.__apiRequestLog.length - API_REQUEST_LOG_MAX)
  }
}

/** Warn once when the same endpoint is hit many times within 1s (helps catch tight loops). */
const burstTimestampsByKey = new Map<string, number[]>()
function noteRequestBurst(method: string, url: string): void {
  if (!isApiDebug()) return
  const key = `${method} ${url}`
  const now = Date.now()
  let arr = burstTimestampsByKey.get(key)
  if (!arr) {
    arr = []
    burstTimestampsByKey.set(key, arr)
  }
  arr.push(now)
  while (arr.length > 0 && now - arr[0]! > 1000) {
    arr.shift()
  }
  if (arr.length === 16) {
    console.warn(
      `[API Client] Burst: ${arr.length} identical requests within 1s — possible loop: ${key}`,
    )
  }
}

export interface FontItem {
  id: string;
  fontId: string;
  name: string;
  displayName: string;
  fontFamily?: string | null;
  brandId?: string | null;
  category?: string | null;
  tags: string[];
  source?: string | null;
  weights?: string[];
  styles?: string[];
  isWhitelisted: boolean;
  woff2Url?: string | null;
  woff2UrlItalic?: string | null;
  thumbnailUrl?: string | null;
  /** 1920×1080 specimen (name + alphabet); shown after square in font modal */
  thumbnailLandscapeUrl?: string | null;
  usageImageUrls: string[];
  url?: string | null;
  description?: string | null;
  designer?: string | null;
  fontFilePath?: string | null;
  createdAt: string;
  updatedAt: string;
  brand?: { id: string; name: string; code: string } | null;
}

/** Moodboard item with optional layout/crop (normalized 0-1). */
export interface MoodboardItemWithLayout {
  id: string
  moodboardId: string
  itemType: 'image' | 'color' | 'copy'
  itemData: string
  order: number
  pageNumber?: number
  positionX?: number | null
  positionY?: number | null
  scale?: number | null
  cropLeft?: number | null
  cropTop?: number | null
  cropWidth?: number | null
  cropHeight?: number | null
  createdAt: string
}

/** Moodboard with items (for GET list/detail). */
export interface MoodboardWithItems {
  id: string
  assetId: string
  name: string
  description?: string | null
  canvasWidth?: number | null
  canvasHeight?: number | null
  exportDpi?: number | null
  items?: MoodboardItemWithLayout[]
  createdAt: string
  updatedAt: string
}

class ApiClient {
  private client: AxiosInstance
  private token: string | null = null

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || '/api',
      timeout: 120000, // 2 minutes - increased for vision model processing
      withCredentials: true,
    })

    // Load token from localStorage on initialization
    this.token = localStorage.getItem('auth_token')
    if (isApiDebug()) {
      console.log(
        '[API Client]',
        this.token ? 'Token loaded from localStorage' : 'No token in localStorage',
      )
    }

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const method = (config.method ?? 'get').toUpperCase()
        const path = config.url ?? ''
        if (isApiDebug()) {
          console.log(`[API Client] → ${method} ${path}`)
          pushApiRequestLog({ t: Date.now(), method, url: path })
          noteRequestBurst(method, path)
        }
        // Add auth token to Authorization header if available
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`
        } else if (isApiDebug()) {
          console.warn(`[API Client] No auth token for ${method} ${path}`)
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error) => {
        const errorData = error.response?.data
        const errorMessage = errorData?.message || errorData?.error || error.message
        if (isApiDebug()) {
          console.error('[API Client] Error:', errorMessage, {
            status: error.response?.status,
            url: error.config?.url,
            method: error.config?.method,
            data: errorData,
          })
        } else {
          console.error('API Error:', errorMessage)
        }
        // Only clear token on 401 (Unauthorized), not 403 (Forbidden)
        // 403 means the user is authenticated but not authorized for this specific resource
        // 401 means the authentication token is invalid/expired
        if (error.response?.status === 401) {
          if (isApiDebug()) {
            console.error('[API Client] Authentication failed (401) - clearing token')
          }
          this.clearToken()
        }
        return Promise.reject(error)
      }
    )
  }

  /**
   * Store authentication token
   */
  private setToken(token: string): void {
    this.token = token
    localStorage.setItem('auth_token', token)
    if (isApiDebug()) {
      console.log('[API Client] Token stored in localStorage')
    }
  }

  /**
   * Clear authentication token
   */
  private clearToken(): void {
    this.token = null
    localStorage.removeItem('auth_token')
    if (isApiDebug()) {
      console.log('[API Client] Token cleared from localStorage')
    }
  }

  private getActiveBrandId(): string | undefined {
    return localStorage.getItem('selectedBrandId') || undefined
  }

  // Authentication
  async login(credentials: LoginRequest): Promise<{ user: User; token: string }> {
    const response = await this.client.post('/auth/login', credentials)
    if (isApiDebug()) {
      console.log('[API Client] Login response:', response.data)
    }
    // Store the token
    if (response.data.token) {
      if (isApiDebug()) {
        console.log('[API Client] Setting token from login response')
      }
      this.setToken(response.data.token)
    } else {
      console.error('[API Client] No token in login response!')
    }
    return response.data
  }

  async register(userData: RegisterRequest): Promise<{ user: User; token: string }> {
    const response = await this.client.post('/auth/register', userData)
    // Store the token
    if (response.data.token) {
      this.setToken(response.data.token)
    }
    return response.data
  }

  async logout(): Promise<void> {
    await this.client.post('/auth/logout')
    // Clear the token
    this.clearToken()
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get('/auth/me')
    // The backend returns { user: {...} }, so we need to access response.data.user
    return response.data.user || response.data
  }

  // Image upload
  async uploadImage(file: File, brandId?: string, projectId?: string): Promise<{ url: string; id: string }> {
    const resolvedBrandId = brandId || this.getActiveBrandId()
    const formData = new FormData()
    formData.append('image', file)
    if (resolvedBrandId) formData.append('brandId', resolvedBrandId)
    if (projectId) formData.append('projectId', projectId)
    
    const response = await this.client.post('/v1/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  // Color palette extraction
  async extractColorPalette(request: ColorPaletteRequest): Promise<ColorPaletteResponse> {
    const response = await this.client.post('/v1/colors/palette', request)
    return response.data
  }

  async getDominantColor(image: string): Promise<ColorInfo> {
    const response = await this.client.post('/v1/colors/dominant', { image })
    return response.data
  }

  async extractColorPaletteFromUrl(imageUrl: string, options?: Partial<ColorPaletteRequest>): Promise<ColorPaletteResponse> {
    const response = await this.client.post('/v1/colors/from-url', { 
      imageUrl, 
      colorCount: options?.colorCount || 8,
      includeHex: options?.includeHex ?? true,
      includeRgb: options?.includeRgb ?? true,
      includeHsl: options?.includeHsl ?? false,
    })
    return response.data
  }

  // Vision analysis
  async analyzeImage(image: string, prompt?: string): Promise<{ response: string }> {
    const response = await this.client.post('/v1/ollama/analyze-image', {
      image,
      prompt: prompt || 'Describe this image in detail',
    })
    return response.data
  }

  async describeImage(image: string): Promise<{ jobId: string; status: string }> {
    const response = await this.client.post('/v1/ollama/describe-image', { image })
    return response.data
  }

  async describeImageWithOpenAI(
    image: string, 
    promptLevel: 'fast' | 'medium' | 'detailed' = 'medium',
    useBackup: boolean = false
  ): Promise<{
    description: string;
    keywords: string[];
    model: 'gpt-5.2' | 'gpt-5-mini';
    usedFallback: boolean;
    processingTime: number;
    promptLevel: string;
    data: any;
  }> {
    const response = await this.client.post('/v1/openai/describe-image', {
      image,
      promptLevel,
      useBackup
    })
    return response.data
  }

  async getJobStatus(jobId: string): Promise<{
    jobId: string;
    status: 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
    result?: any;
    description?: string;
    fullDescription?: string;
    error?: string;
  }> {
    const response = await this.client.get(`/v1/ollama/jobs/${jobId}/status`)
    return response.data
  }

  async extractKeywords(image: string, count: number = 15): Promise<{ keywords: string[]; count: number }> {
    const response = await this.client.post('/v1/ollama/extract-keywords', { image, count })
    return response.data
  }

  async detectObjects(image: string, options?: { 
    model?: string; 
    confidence?: number; 
    iouThreshold?: number;
  }): Promise<{ 
    detections: Array<{
      name: string;
      confidence: number;
      bbox: { x: number; y: number; width: number; height: number };
    }>;
    totalDetections: number;
    processingTime: number;
    imageDimensions?: {
      width: number;
      height: number;
    };
  }> {
    const response = await this.client.post('/v1/vision/detect-objects', { 
      image, 
      model: options?.model || 'yolov8n',
      confidence: options?.confidence || 0.5,
      iouThreshold: options?.iouThreshold || 0.45
    })
    return response.data
  }

  async detectAll(image: string, imageId?: string, options?: {
    model?: string;
    confidence?: number;
    iouThreshold?: number;
  }): Promise<{
    success: boolean;
    objects: { detections: Array<{ name: string; bbox: number[]; confidence: number }>; total: number };
    people: { detections: Array<{ name: string; rect: number[]; confidence: number; face: boolean }>; total: number };
    processingTime: number;
  }> {
    const response = await this.client.post('/v1/vision/detect-all', {
      image,
      imageId,
      model: options?.model || 'yolov8n',
      confidence: options?.confidence || 0.5,
      iouThreshold: options?.iouThreshold || 0.45
    })
    return response.data
  }

  async scanText(imageId: string): Promise<{
    success: boolean;
    imageId: string;
    total: number;
    text: Array<{ bbox: number[]; text: string; confidence: number }>;
    processingTime: number;
  }> {
    const response = await this.client.post('/v1/images/scan-text', { imageId })
    return response.data
  }

  // Python Worker: Fast image description (BLIP)
  async describeImageWithPython(image: string, imageId?: string): Promise<{
    description: string;
    model: string;
    processingTime: number;
    imageId?: string;
  }> {
    const response = await this.client.post('/v1/python/describe-image', { 
      image,
      imageId
    })
    return response.data
  }

  // Python Worker: Feature detection (OpenCV)
  async detectFeatures(image: string, imageId?: string): Promise<{
    detectionData: any;
    model: string;
    processingTime: number;
    imageId?: string;
  }> {
    const response = await this.client.post('/v1/python/detect-features', { 
      image,
      imageId
    })
    return response.data
  }

  // Python Worker: Composition analysis (Kosmos + OpenCV)
  async analyzeComposition(image: string, imageId?: string): Promise<{
    layoutData: any;
    detectionData: any;
    processingTime: number;
    imageId?: string;
  }> {
    const response = await this.client.post('/v1/python/analyze-composition', { 
      image,
      imageId
    })
    return response.data
  }

  // Python Worker: Full analysis (BLIP + Kosmos + OpenCV)
  async analyzeFull(image: string, imageId?: string): Promise<{
    description: string;
    layoutData: any;
    detectionData: any;
    processingTime: number;
    imageId?: string;
  }> {
    const response = await this.client.post('/v1/python/analyze-full', { 
      image,
      imageId
    })
    return response.data
  }

  // Health check
  async healthCheck(): Promise<{ ok: boolean }> {
    const response = await this.client.get('/health')
    return response.data
  }

  // Image gallery
  async fetchImages(options?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
    brandId?: string;
    projectId?: string;
    colorSimilarityImageId?: string;
    galleryIds?: string[];
    includeGalleries?: boolean;
  }): Promise<{
    images: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options?.search) params.append('search', options.search);
    if (options?.brandId) params.append('brandId', options.brandId);
    if (options?.projectId) params.append('projectId', options.projectId);
    if (options?.colorSimilarityImageId) params.append('colorSimilarityImageId', options.colorSimilarityImageId);
    if (options?.galleryIds && options.galleryIds.length > 0) {
      options.galleryIds.forEach(id => params.append('galleryIds', id));
    }
    if (options?.includeGalleries) params.append('includeGalleries', 'true');
    
    const response = await this.client.get(`/v1/images?${params.toString()}`);
    return response.data;
  }

  // Clear all images for current user (for testing)
  async clearAllImages(): Promise<{ success: boolean; deletedCount: number; message: string }> {
    const response = await this.client.delete('/v1/images/clear-all');
    return response.data;
  }

  // Delete a single image
  async deleteImage(imageId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete(`/v1/images/${imageId}`);
    return response.data;
  }

  async restoreImage(imageId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.patch(`/v1/images/${imageId}/restore`, {});
    return response.data;
  }

  async restoreAllImages(): Promise<{ message: string; restoredCount: number }> {
    const response = await this.client.patch('/v1/images/trash/restore', {});
    return response.data;
  }

  async emptyTrash(): Promise<{ message: string; deletedCount: number; filesDeleted: number; errors?: string[] }> {
    const response = await this.client.delete('/v1/images/empty-trash');
    return response.data;
  }

  // Batch upload images
  async batchUploadImages(
    files: File[], 
    thumbnailOnly: boolean = false,
    brandId?: string,
    projectId?: string,
    folderPath?: string
  ): Promise<{
    success: boolean;
    results: Array<{
      imageId: string;
      uploadId: string;
      filename: string;
      url: string;
      needsOriginalUpload: boolean;
      duplicate?: boolean;
      existingImage?: {
        id: string;
        basename: string;
        url: string;
        previewUrl?: string;
        iconUrl?: string;
        dateUploaded?: string;
      };
      imageRecord?: {
        id: string;
        width: number;
        height: number;
        format: string;
        iconUrl?: string;
        previewUrl?: string;
        url: string;
      };
      error?: string;
    }>;
    count: number;
    duplicates?: number;
    message: string;
  }> {
    const resolvedBrandId = brandId || this.getActiveBrandId();
    console.log('[API Client] Starting batch upload:', { fileCount: files.length, thumbnailOnly, brandId: resolvedBrandId, projectId });
    const formData = new FormData();
    files.forEach((file, index) => {
      console.log(`[API Client] Appending file ${index + 1}/${files.length}:`, file.name, file.size, file.type);
      formData.append('file', file);
    });
    if (resolvedBrandId) formData.append('brandId', resolvedBrandId);
    if (projectId) formData.append('projectId', projectId);
    if (folderPath) formData.append('folderPath', folderPath);
    
    const params = thumbnailOnly ? '?thumbnailOnly=true' : '';
    const url = `/v1/upload/batch${params}`;
    console.log('[API Client] Sending batch upload request to:', url);
    
    try {
      // Don't set Content-Type header - axios will set it automatically with boundary
      const response = await this.client.post(url, formData, {
        timeout: 300000, // 5 minutes timeout
      });
      console.log('[API Client] Batch upload response received:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[API Client] Batch upload error:', error);
      if (error.response) {
        console.error('[API Client] Error response:', error.response.status, error.response.data);
      }
      throw error;
    }
  }

  // Upload original full-size image
  async uploadOriginalImage(
    imageId: string,
    file: File
  ): Promise<{
    success: boolean;
    imageId: string;
    imageRecord: {
      id: string;
      width: number;
      height: number;
      format: string;
      iconUrl?: string;
      previewUrl?: string;
      url: string;
    };
  }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('imageId', imageId);
    
    const response = await this.client.post('/v1/upload/originals', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Fetch processing jobs (for UI polling or queue view)
  async getImageProcessingJobs(params: {
    imageIds?: string[];
    jobIds?: string[];
    activeOnly?: boolean;
    /** Optional cache-buster for polling (e.g. Date.now()) so progress updates are not served from cache */
    _t?: number;
  }): Promise<{
    jobs: Array<{
      id: string;
      refImageId: string;
      jobType: string;
      status: string;
      priority: number;
      metadata?: any;
      error?: string | null;
      createdAt?: string;
      startedAt?: string;
      completedAt?: string;
      refImage?: { id: string; basename: string; iconUrl: string | null };
    }>;
  }> {
    const search = new URLSearchParams();
    if (params.imageIds?.length) {
      search.append('imageIds', params.imageIds.join(','));
    }
    if (params.jobIds?.length) {
      search.append('jobIds', params.jobIds.join(','));
    }
    if (params.activeOnly === true) {
      search.append('activeOnly', 'true');
    }
    if (params._t != null) {
      search.append('_t', String(params._t));
    }
    const response = await this.client.get(`/v1/image-processing-jobs?${search.toString()}`);
    return response.data;
  }

  // Batch analyze images
  async batchAnalyzeImages(imageIds: string[], chunkSize = 50): Promise<{
    success: boolean;
    jobIds: string[];
    count: number;
    message: string;
  }> {
    const allJobIds: string[] = [];
    for (let i = 0; i < imageIds.length; i += chunkSize) {
      const chunk = imageIds.slice(i, i + chunkSize);
      const response = await this.client.post('/v1/images/batch-analyze', { imageIds: chunk });
      allJobIds.push(...(response.data.jobIds ?? []));
    }
    return {
      success: true,
      jobIds: allJobIds,
      count: allJobIds.length,
      message: `Queued ${allJobIds.length} analysis jobs`,
    };
  }

  // Update image rating
  async updateImageRating(imageId: string, rating: number): Promise<{
    success: boolean;
    imageId: string;
    rating: number | null;
  }> {
    const response = await this.client.patch(`/v1/images/${imageId}/rating`, { rating });
    return response.data;
  }

  // Update image tags
  async updateImageTags(imageId: string, tags: string[]): Promise<{
    success: boolean;
    imageId: string;
    tags: string[];
  }> {
    const response = await this.client.patch(`/v1/images/${imageId}/tags`, { tags });
    return response.data;
  }

  // Update image keywords
  async updateImageKeywords(imageId: string, keywords: string[]): Promise<{
    success: boolean;
    imageId: string;
    keywords: string[];
  }> {
    const response = await this.client.patch(`/v1/images/${imageId}/keywords`, { keywords });
    return response.data;
  }

  // Rename image (uses BLIP to generate name)
  async renameImage(imageId: string): Promise<{
    success: boolean;
    imageId: string;
    oldBasename: string;
    newBasename: string;
    blipWord: string;
    blipDescription: string;
    originalFilename?: string;
  }> {
    const response = await this.client.patch(`/v1/images/${imageId}/rename`);
    return response.data;
  }

  // Get images for culling
  async getCullingImages(filters: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    paletteFilter?: string;
    keywordSearch?: string;
    minRating?: number;
    tags?: string[];
  }): Promise<{
    images: Array<{
      id: string;
      basename: string;
      url: string;
      previewUrl?: string;
      iconUrl?: string;
      width: number;
      height: number;
      format: string;
      rating: number | null;
      dominantColor: string | null;
      palette: string[] | null; // Array from backend (parsed from JSON)
      shortDescription: string | null;
      fullDescription: string | null;
      keywords: string[];
      tags: string[];
      ingestAt: string;
      dateUploaded: string | null;
      exifDate: string | null;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters.paletteFilter) params.append('paletteFilter', filters.paletteFilter);
    if (filters.keywordSearch) params.append('keywordSearch', filters.keywordSearch);
    if (filters.minRating) params.append('minRating', filters.minRating.toString());
    if (filters.tags) {
      filters.tags.forEach(tag => params.append('tags', tag));
    }
    
    const response = await this.client.get(`/v1/images/culling?${params.toString()}`);
    return response.data;
  }

  // User management (admin only)
  async listUsers(): Promise<{ users: User[] }> {
    const response = await this.client.get('/v1/users');
    return response.data;
  }

  async createUser(email: string, name?: string, role?: string): Promise<{ user: User; message: string }> {
    const response = await this.client.post('/v1/users', { email, name, role });
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response = await this.client.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  }

  // Database statistics (admin only)
  async getRefImages(options?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    userId?: string;
    brandId?: string;
    projectId?: string;
    search?: string;
  }): Promise<{
    refImages: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options?.userId) params.append('userId', options.userId);
    if (options?.brandId) params.append('brandId', options.brandId);
    if (options?.projectId) params.append('projectId', options.projectId);
    if (options?.search) params.append('search', options.search);
    
    const response = await this.client.get(`/v1/admin/ref-images?${params.toString()}`);
    return response.data;
  }

  async getDbStats(): Promise<{
    stats: {
      User: number;
      Image: number;
      RefImage: number;
      Job: number;
      Template: number;
      Comp: number;
      CompLayer: number;
      Tag: number;
      TagOnImage: number;
      ImageProcessingJob: number;
    };
  }> {
    const response = await this.client.get('/v1/admin/db-stats');
    return response.data;
  }

  /** Admin: ping external integrations (Apify, S3, Ollama, workers, AI APIs). */
  async getIntegrationsHealth(): Promise<{
    checkedAt: string;
    checks: Array<{
      id: string;
      label: string;
      ok: boolean;
      latencyMs: number;
      detail?: string;
    }>;
  }> {
    const response = await this.client.get('/v1/admin/integrations-health');
    return response.data;
  }

  // ============================================================
  // Brand Management
  // ============================================================

  async getBrands(options?: { page?: number; limit?: number }): Promise<{
    brands: Array<{
      id: string;
      name: string;
      code: string;
      description?: string;
      createdAt: string;
      updatedAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    const response = await this.client.get(`/v1/brands?${params.toString()}`);
    return response.data;
  }

  async getBrand(id: string): Promise<{
    brand: {
      id: string;
      name: string;
      code: string;
      description?: string;
      contactName?: string;
      contactEmail?: string;
      phone?: string;
      address?: string;
      website?: string;
      igHandle?: string;
      fbHandle?: string;
      logoUrls?: string;
      colors?: string;
      typefaces?: string;
      usp?: string;
      notes?: string;
      voiceTone?: string;
      taglines?: string;
      elevatorPitch?: string;
      teamMembers?: string;
      projects?: Array<{
        id: string;
        name: string;
        code: string;
      }>;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.get(`/v1/brands/${id}`);
    return response.data;
  }

  async createBrand(data: {
    name: string;
    code: string;
    brandCode?: string;
    description?: string;
    contactName?: string;
    contactEmail?: string;
    phone?: string;
    address?: string;
    website?: string;
    igHandle?: string;
    fbHandle?: string;
    logoUrls?: string | string[];
    colors?: string | Array<{ name: string; colors: Array<{ name?: string; hex: string; keywords?: string[] }> }>;
    typefaces?: string | Array<{ typefaceName: string; fontId: string; foundry: string }>;
    usp?: string;
    notes?: string;
    voiceTone?: string;
    taglines?: string | string[];
    elevatorPitch?: string;
    positiveBrandKeywords?: string | string[];
    negativeBrandKeywords?: string | string[];
    missionStatement?: string;
    teamMembers?: string | Array<{ name: string; role: string; initials: string }>;
  }): Promise<{
    brand: {
      id: string;
      name: string;
      code: string;
      description?: string;
      contactName?: string;
      contactEmail?: string;
      phone?: string;
      address?: string;
      website?: string;
      igHandle?: string;
      fbHandle?: string;
      logoUrls?: string;
      colors?: string;
      typefaces?: string;
      usp?: string;
      notes?: string;
      voiceTone?: string;
      taglines?: string;
      elevatorPitch?: string;
      teamMembers?: string;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.post('/v1/brands', data);
    return response.data;
  }

  async deleteBrand(id: string): Promise<{
    message: string;
    deleted?: {
      comps: number;
      images: number;
      layouts: number;
      projects: number;
      assets: number;
      deliverables: number;
      versions: number;
    };
  }> {
    const response = await this.client.delete(`/v1/brands/${id}`);
    return response.data;
  }

  async updateBrand(id: string, data: {
    name?: string;
    code?: string;
    description?: string;
    contactName?: string;
    contactEmail?: string;
    phone?: string;
    address?: string;
    website?: string;
    igHandle?: string;
    fbHandle?: string;
    logoUrls?: string | string[];
    colors?: string | Array<{ name: string; colors: Array<{ name?: string; hex: string; keywords?: string[] }> }>;
    typefaces?: string | Array<{ typefaceName: string; fontId: string; foundry: string }>;
    usp?: string;
    notes?: string;
    voiceTone?: string;
    taglines?: string | string[];
    missionStatement?: string;
    teamMembers?: string | Array<{ name: string; role: string; initials: string }>;
  }): Promise<{
    brand: {
      id: string;
      name: string;
      code: string;
      description?: string;
      contactName?: string;
      contactEmail?: string;
      phone?: string;
      address?: string;
      website?: string;
      igHandle?: string;
      fbHandle?: string;
      logoUrls?: string;
      colors?: string;
      typefaces?: string;
      usp?: string;
      notes?: string;
      voiceTone?: string;
      taglines?: string;
      elevatorPitch?: string;
      teamMembers?: string;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.patch(`/v1/brands/${id}`, data);
    return response.data;
  }

  // ============================================================
  // Project Management
  // ============================================================

  async getProjects(options?: { page?: number; limit?: number; brandId?: string }): Promise<{
    projects: Array<{
      id: string;
      name: string;
      code: string;
      brandId: string;
      brand?: {
        id: string;
        name: string;
        code: string;
      };
      description?: string;
      createdAt: string;
      updatedAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.brandId) params.append('brandId', options.brandId);
    const response = await this.client.get(`/v1/projects?${params.toString()}`);
    return response.data;
  }

  async getProject(id: string): Promise<{
    project: {
      id: string;
      name: string;
      code: string;
      brandId: string;
      brand?: {
        id: string;
        name: string;
        code: string;
      };
      description?: string;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.get(`/v1/projects/${id}`);
    return response.data;
  }

  async createProject(data: { name: string; code: string; brandId: string; description?: string }): Promise<{
    project: {
      id: string;
      name: string;
      code: string;
      brandId: string;
      brand?: {
        id: string;
        name: string;
        code: string;
      };
      description?: string;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.post('/v1/projects', data);
    return response.data;
  }

  async updateProject(id: string, data: { name?: string; code?: string; description?: string }): Promise<{
    project: {
      id: string;
      name: string;
      code: string;
      brandId: string;
      brand?: {
        id: string;
        name: string;
        code: string;
      };
      description?: string;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.patch(`/v1/projects/${id}`, data);
    return response.data;
  }

  async deleteProject(id: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/v1/projects/${id}`);
    return response.data;
  }

  // ============================================================
  // Asset Management
  // ============================================================

  async getAssets(options?: { page?: number; limit?: number; projectId?: string; type?: string }): Promise<{
    assets: Array<{
      id: string;
      projectId: string;
      type: string | null;
      name: string;
      filePath?: string;
      metadata?: string;
      versionNumber?: number;
      project?: {
        id: string;
        name: string;
        code: string;
        brand?: {
          id: string;
          name: string;
          code: string;
        };
      };
      moodboards?: any[];
      createdAt: string;
      updatedAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.projectId) params.append('projectId', options.projectId);
    if (options?.type) params.append('type', options.type);
    const response = await this.client.get(`/v1/assets?${params.toString()}`);
    return response.data;
  }

  async getAsset(id: string): Promise<{
    asset: {
      id: string;
      projectId: string;
      type: string | null;
      name: string;
      filePath?: string;
      metadata?: string;
      versionNumber?: number;
      project?: any;
      moodboards?: any[];
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.get(`/v1/assets/${id}`);
    return response.data;
  }

  async createAsset(data: {
    projectId: string;
    type?: string;
    name: string;
    metadata?: string;
    versionNumber?: number;
  }): Promise<{
    asset: {
      id: string;
      projectId: string;
      type: string | null;
      name: string;
      filePath?: string;
      metadata?: string;
      versionNumber?: number;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.post('/v1/assets', data);
    return response.data;
  }

  async uploadAsset(
    file: File,
    projectId: string,
    options?: {
      type?: string;
      name?: string;
      versionNumber?: number;
    }
  ): Promise<{
    asset: {
      id: string;
      projectId: string;
      type: string | null;
      name: string;
      filePath?: string;
      metadata?: string;
      versionNumber?: number;
      createdAt: string;
      updatedAt: string;
    };
    fileUrl: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId);
    if (options?.type) formData.append('type', options.type);
    if (options?.name) formData.append('name', options.name);
    if (options?.versionNumber) formData.append('versionNumber', options.versionNumber.toString());

    const response = await this.client.post('/v1/assets/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateAsset(
    id: string,
    data: {
      name?: string;
      type?: string;
      metadata?: string;
      versionNumber?: number;
    }
  ): Promise<{
    asset: {
      id: string;
      projectId: string;
      type: string | null;
      name: string;
      filePath?: string;
      metadata?: string;
      versionNumber?: number;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.patch(`/v1/assets/${id}`, data);
    return response.data;
  }

  async deleteAsset(id: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/v1/assets/${id}`);
    return response.data;
  }

  async getAssetVersions(projectId: string, name: string): Promise<{
    assets: Array<{
      id: string;
      projectId: string;
      type: string | null;
      name: string;
      versionNumber: number;
      filePath?: string;
      metadata?: string;
      createdAt: string;
      updatedAt: string;
    }>;
  }> {
    const response = await this.client.get(`/v1/assets/${projectId}/${encodeURIComponent(name)}/versions`);
    return response.data;
  }

  // ============================================================
  // Font Management
  // ============================================================

  async getFonts(options?: {
    brandId?: string;
    isWhitelisted?: boolean;
    category?: string;
    search?: string;
  }): Promise<{ fonts: FontItem[] }> {
    const params = new URLSearchParams();
    if (options?.brandId) params.append('brandId', options.brandId);
    if (options?.isWhitelisted !== undefined) params.append('isWhitelisted', String(options.isWhitelisted));
    if (options?.category) params.append('category', options.category);
    if (options?.search) params.append('search', options.search);
    const response = await this.client.get(`/v1/fonts${params.toString() ? `?${params.toString()}` : ''}`);
    return response.data;
  }

  async getFont(id: string): Promise<{ font: FontItem }> {
    const response = await this.client.get(`/v1/fonts/${id}`);
    return response.data;
  }

  async fetchGalleryFonts(
    galleryId: string,
    options?: {
      page?: number;
      limit?: number;
      search?: string;
      category?: string;
      isWhitelisted?: boolean;
    }
  ): Promise<{
    fonts: FontItem[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', String(options.page));
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.search) params.append('search', options.search);
    if (options?.category) params.append('category', options.category);
    if (options?.isWhitelisted !== undefined) params.append('isWhitelisted', String(options.isWhitelisted));
    const response = await this.client.get(`/v1/galleries/${galleryId}/fonts?${params.toString()}`);
    return response.data;
  }

  async uploadFontThumbnail(fontId: string, file: File): Promise<{ font: FontItem }> {
    const form = new FormData();
    form.append('thumbnail', file);
    const response = await this.client.post(`/v1/fonts/${fontId}/thumbnail`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async uploadFontUsageImages(fontId: string, files: File[]): Promise<{ font: FontItem; added: number }> {
    const form = new FormData();
    for (const file of files) form.append('images', file);
    const response = await this.client.post(`/v1/fonts/${fontId}/usage-images`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteFontUsageImage(fontId: string, url: string): Promise<{ font: FontItem }> {
    const response = await this.client.delete(`/v1/fonts/${fontId}/usage-images`, { data: { url } });
    return response.data;
  }

  async updateFont(id: string, data: Partial<FontItem>): Promise<{ font: FontItem }> {
    const response = await this.client.patch(`/v1/fonts/${id}`, data);
    return response.data;
  }

  async describeFont(id: string): Promise<{
    suggestions: { description: string; category: string; tags: string[]; designer: string };
  }> {
    const response = await this.client.post(`/v1/fonts/${id}/describe`);
    return response.data;
  }

  async describeFontFamily(familyName: string, variants: Array<{ id: string; displayName: string; weight?: string | null; style?: string | null }>, url?: string | null): Promise<{
    suggestions: { description: string; category: string; tags: string[]; designer: string };
  }> {
    const response = await this.client.post('/v1/fonts/describe-family', { familyName, variants, url });
    return response.data;
  }

  // ============================================================
  // Deliverable Management
  // ============================================================

  async getDeliverables(options?: { page?: number; limit?: number; projectId?: string; versionId?: string; type?: string; name?: string }): Promise<{
    deliverables: Array<{
      id: string;
      projectId: string;
      versionId: string;
      type: string | null;
      name: string;
      versionNumber: number;
      filePath?: string;
      metadata?: string;
      width?: number;
      height?: number;
      project?: any;
      version?: any;
      createdAt: string;
      updatedAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.projectId) params.append('projectId', options.projectId);
    if (options?.versionId) params.append('versionId', options.versionId);
    if (options?.type) params.append('type', options.type);
    if (options?.name) params.append('name', options.name);
    const response = await this.client.get(`/v1/deliverables?${params.toString()}`);
    return response.data;
  }

  async getDeliverable(id: string): Promise<{
    deliverable: {
      id: string;
      projectId: string;
      versionId: string;
      type: string | null;
      name: string;
      versionNumber: number;
      filePath?: string;
      metadata?: string;
      width?: number;
      height?: number;
      project?: any;
      version?: any;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.get(`/v1/deliverables/${id}`);
    return response.data;
  }

  async createDeliverable(data: {
    projectId: string;
    versionId: string;
    type?: string;
    name: string;
    metadata?: string;
    width?: number;
    height?: number;
    versionNumber?: number;
  }): Promise<{
    deliverable: {
      id: string;
      projectId: string;
      versionId: string;
      type: string | null;
      name: string;
      versionNumber: number;
      filePath?: string;
      metadata?: string;
      width?: number;
      height?: number;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.post('/v1/deliverables', data);
    return response.data;
  }

  async updateDeliverable(
    id: string,
    data: {
      name?: string;
      type?: string;
      metadata?: string;
      filePath?: string;
      width?: number;
      height?: number;
      versionNumber?: number;
    }
  ): Promise<{
    deliverable: {
      id: string;
      projectId: string;
      versionId: string;
      type: string | null;
      name: string;
      versionNumber: number;
      filePath?: string;
      metadata?: string;
      width?: number;
      height?: number;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.patch(`/v1/deliverables/${id}`, data);
    return response.data;
  }

  async deleteDeliverable(id: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/v1/deliverables/${id}`);
    return response.data;
  }

  async getDeliverableVersions(projectId: string, name: string): Promise<{
    deliverables: Array<{
      id: string;
      projectId: string;
      versionId: string;
      type: string | null;
      name: string;
      versionNumber: number;
      filePath?: string;
      metadata?: string;
      width?: number;
      height?: number;
      createdAt: string;
      updatedAt: string;
    }>;
  }> {
    const response = await this.client.get(`/v1/deliverables/${projectId}/${encodeURIComponent(name)}/versions`);
    return response.data;
  }

  async publishComp(compId: string, deliverableName: string, projectId: string, deliverableType?: string): Promise<{
    version: any;
    deliverable: any;
  }> {
    const response = await this.client.post(`/v1/comps/${compId}/publish`, {
      deliverableName,
      deliverableType,
      projectId
    });
    return response.data;
  }

  // ============================================================
  // Color Palette Management
  // ============================================================

  async getColorPalettes(projectId: string): Promise<{
    assets: Array<{
      id: string;
      projectId: string;
      type: string | null;
      name: string;
      metadata?: string;
      createdAt: string;
      updatedAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    return this.getAssets({ projectId, type: 'colorPalette', limit: 100 });
  }

  async getPaletteColors(assetId: string): Promise<{
    colors: Array<{
      hex: string;
      rgb?: { r: number; g: number; b: number };
      hsl?: { h: number; s: number; l: number };
      percentage?: number;
    }>;
  }> {
    const response = await this.client.get(`/v1/assets/${assetId}/colors`);
    return response.data;
  }

  async addColorToPalette(
    assetId: string,
    color: {
      hex: string;
      rgb?: { r: number; g: number; b: number };
      hsl?: { h: number; s: number; l: number };
      percentage?: number;
    }
  ): Promise<{
    asset: any;
    colors: Array<{
      hex: string;
      rgb?: { r: number; g: number; b: number };
      hsl?: { h: number; s: number; l: number };
      percentage?: number;
    }>;
  }> {
    const response = await this.client.post(`/v1/assets/${assetId}/colors`, color);
    return response.data;
  }

  async updatePaletteColors(
    assetId: string,
    colors: Array<{
      hex: string;
      rgb?: { r: number; g: number; b: number };
      hsl?: { h: number; s: number; l: number };
      percentage?: number;
    }>
  ): Promise<{
    asset: any;
    colors: Array<{
      hex: string;
      rgb?: { r: number; g: number; b: number };
      hsl?: { h: number; s: number; l: number };
      percentage?: number;
    }>;
  }> {
    const response = await this.client.patch(`/v1/assets/${assetId}/colors`, { colors });
    return response.data;
  }

  async deletePaletteColor(assetId: string, index: number): Promise<{
    asset: any;
    colors: Array<{
      hex: string;
      rgb?: { r: number; g: number; b: number };
      hsl?: { h: number; s: number; l: number };
      percentage?: number;
    }>;
  }> {
    const response = await this.client.delete(`/v1/assets/${assetId}/colors/${index}`);
    return response.data;
  }

  async updatePaletteLabels(
    assetId: string,
    labels: {
      primary?: string;
      secondary?: string;
      accent?: string;
      light?: string;
      dark?: string;
      neutral?: string;
    }
  ): Promise<{
    asset: any;
    labels: {
      primary?: string;
      secondary?: string;
      accent?: string;
      light?: string;
      dark?: string;
      neutral?: string;
    };
  }> {
    const response = await this.client.patch(`/v1/assets/${assetId}/labels`, { labels });
    return response.data;
  }

  // ============================================================
  // Moodboard Management
  // ============================================================

  async getMoodboards(options?: { assetId?: string }): Promise<{
    moodboards: Array<MoodboardWithItems>;
  }> {
    const activeBrandId = this.getActiveBrandId();
    const params = new URLSearchParams();
    if (options?.assetId) params.append('assetId', options.assetId);
    if (activeBrandId) params.append('brandId', activeBrandId);
    const response = await this.client.get(`/v1/moodboards?${params.toString()}`);
    return response.data;
  }

  async getMoodboard(id: string): Promise<{
    moodboard: MoodboardWithItems & { asset?: any };
  }> {
    const activeBrandId = this.getActiveBrandId();
    const params = new URLSearchParams();
    if (activeBrandId) params.append('brandId', activeBrandId);
    const response = await this.client.get(`/v1/moodboards/${id}?${params.toString()}`);
    return response.data;
  }

  async createMoodboard(data: {
    assetId: string;
    name: string;
    description?: string;
    canvasWidth?: number;
    canvasHeight?: number;
    exportDpi?: number;
  }): Promise<{ moodboard: MoodboardWithItems }> {
    const activeBrandId = this.getActiveBrandId();
    const response = await this.client.post('/v1/moodboards', {
      ...data,
      ...(activeBrandId ? { brandId: activeBrandId } : {}),
    });
    return response.data;
  }

  async updateMoodboard(
    id: string,
    data: { name?: string; description?: string; canvasWidth?: number | null; canvasHeight?: number | null; exportDpi?: number | null }
  ): Promise<{ moodboard: MoodboardWithItems }> {
    const activeBrandId = this.getActiveBrandId();
    const response = await this.client.patch(`/v1/moodboards/${id}`, {
      ...data,
      ...(activeBrandId ? { brandId: activeBrandId } : {}),
    });
    return response.data;
  }

  async deleteMoodboard(id: string): Promise<{ message: string }> {
    const activeBrandId = this.getActiveBrandId();
    const params = new URLSearchParams();
    if (activeBrandId) params.append('brandId', activeBrandId);
    const response = await this.client.delete(`/v1/moodboards/${id}?${params.toString()}`);
    return response.data;
  }

  async addFontToMoodboard(moodboardId: string, fontId: string): Promise<{ item: any }> {
    const activeBrandId = this.getActiveBrandId();
    const response = await this.client.post(`/v1/moodboards/${moodboardId}/items`, {
      itemType: 'font',
      itemData: JSON.stringify({ fontId }),
      fontId,
      ...(activeBrandId ? { brandId: activeBrandId } : {}),
    });
    return response.data;
  }

  async addMoodboardItem(
    moodboardId: string,
    data: {
      itemType: 'image' | 'color' | 'copy' | 'font';
      itemData: string;
      fontId?: string;
      order?: number;
    }
  ): Promise<{
    item: {
      id: string;
      moodboardId: string;
      itemType: 'image' | 'color' | 'copy';
      itemData: string;
      order: number;
      createdAt: string;
    };
  }> {
    const activeBrandId = this.getActiveBrandId();
    const response = await this.client.post(`/v1/moodboards/${moodboardId}/items`, {
      ...data,
      ...(activeBrandId ? { brandId: activeBrandId } : {}),
    });
    return response.data;
  }

  async updateMoodboardItem(
    moodboardId: string,
    itemId: string,
    data: {
      itemType?: 'image' | 'color' | 'copy';
      itemData?: string;
      order?: number;
      pageNumber?: number;
      positionX?: number | null;
      positionY?: number | null;
      scale?: number | null;
      cropLeft?: number | null;
      cropTop?: number | null;
      cropWidth?: number | null;
      cropHeight?: number | null;
    }
  ): Promise<{ item: MoodboardItemWithLayout }> {
    const activeBrandId = this.getActiveBrandId();
    const response = await this.client.patch(`/v1/moodboards/${moodboardId}/items/${itemId}`, {
      ...data,
      ...(activeBrandId ? { brandId: activeBrandId } : {}),
    });
    return response.data;
  }

  async deleteMoodboardItem(moodboardId: string, itemId: string): Promise<{ message: string }> {
    const activeBrandId = this.getActiveBrandId();
    const params = new URLSearchParams();
    if (activeBrandId) params.append('brandId', activeBrandId);
    const response = await this.client.delete(`/v1/moodboards/${moodboardId}/items/${itemId}?${params.toString()}`);
    return response.data;
  }

  /**
   * Export moodboard as PSD. Returns blob and suggested filename from Content-Disposition.
   */
  async generateFontWordSpecimens(
    moodboardId: string,
    word: string
  ): Promise<{ items: { moodboardItemId: string; fontId: string; url: string }[] }> {
    const activeBrandId = this.getActiveBrandId();
    const response = await this.client.post(`/v1/moodboards/${moodboardId}/font-word-specimens`, {
      word,
      ...(activeBrandId ? { brandId: activeBrandId } : {}),
    });
    return response.data;
  }

  async exportMoodboardPsd(moodboardId: string): Promise<{ blob: Blob; filename: string }> {
    const activeBrandId = this.getActiveBrandId();
    const response = await this.client.post(`/v1/moodboards/${moodboardId}/export/psd`, {
      ...(activeBrandId ? { brandId: activeBrandId } : {}),
    }, {
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    const disposition = response.headers['content-disposition'];
    const filenameMatch = disposition?.match(/filename="?([^";]+)"?/);
    const filename = filenameMatch ? filenameMatch[1] : 'moodboard.psd';
    return { blob, filename };
  }

  // ============================================================
  // Comp Management
  // ============================================================

  async getComps(options?: { page?: number; limit?: number; projectId?: string; brandId?: string; status?: string }): Promise<{
    comps: Array<{
      id: string;
      name: string;
      description?: string;
      width: number;
      height: number;
      backgroundColor?: string;
      status: string;
      brandId?: string;
      projectId?: string;
      layers: string; // JSON string
      layers_rel?: Array<{
        id: string;
        label: string;
        key: string;
        type: string;
        zIndex: number;
        x: number;
        y: number;
        width?: number;
        height?: number;
        properties: string;
        transferMode: string;
        srcImage?: string;
        visible: boolean;
        opacity: number;
        rotation: number;
      }>;
      user?: {
        id: string;
        email: string;
        name?: string;
      };
      createdAt: string;
      updatedAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.projectId) params.append('projectId', options.projectId);
    if (options?.brandId) params.append('brandId', options.brandId);
    if (options?.status) params.append('status', options.status);
    const response = await this.client.get(`/v1/comps?${params.toString()}`);
    return response.data;
  }

  async getComp(id: string): Promise<{
    comp: {
      id: string;
      name: string;
      description?: string;
      width: number;
      height: number;
      backgroundColor?: string;
      status: string;
      brandId?: string;
      projectId?: string;
      layers: string; // JSON string
      layers_rel?: Array<{
        id: string;
        label: string;
        key: string;
        type: string;
        zIndex: number;
        x: number;
        y: number;
        width?: number;
        height?: number;
        properties: string;
        transferMode: string;
        srcImage?: string;
        visible: boolean;
        opacity: number;
        rotation: number;
      }>;
      user?: {
        id: string;
        email: string;
        name?: string;
      };
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.get(`/v1/comps/${id}`);
    return response.data;
  }

  async createComp(data: {
    name: string;
    description?: string;
    width: number;
    height: number;
    backgroundColor?: string;
    projectId?: string;
    brandId?: string;
    layers?: any[]; // Array of layer objects
  }): Promise<{
    comp: {
      id: string;
      name: string;
      description?: string;
      width: number;
      height: number;
      backgroundColor?: string;
      status: string;
      brandId?: string;
      projectId?: string;
      layers: string;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.post('/v1/comps', data);
    return response.data;
  }

  async updateComp(id: string, data: {
    name?: string;
    description?: string;
    width?: number;
    height?: number;
    backgroundColor?: string;
    projectId?: string;
    brandId?: string;
    layers?: any[];
    status?: string;
  }): Promise<{
    comp: {
      id: string;
      name: string;
      description?: string;
      width: number;
      height: number;
      backgroundColor?: string;
      status: string;
      brandId?: string;
      projectId?: string;
      layers: string;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.patch(`/v1/comps/${id}`, data);
    return response.data;
  }

  async deleteComp(id: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/v1/comps/${id}`);
    return response.data;
  }

  async createCompLayer(compId: string, data: {
    label: string;
    key: string;
    type: string;
    zIndex: number;
    x: number;
    y: number;
    width?: number;
    height?: number;
    properties: string;
    transferMode?: string;
    visible?: boolean;
    opacity?: number;
    rotation?: number;
    srcImage?: string;
  }): Promise<{
    layer: {
      id: string;
      compId: string;
      label: string;
      key: string;
      type: string;
      zIndex: number;
      x: number;
      y: number;
      width?: number;
      height?: number;
      properties: string;
      transferMode: string;
      srcImage?: string;
      visible: boolean;
      opacity: number;
      rotation: number;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.post(`/v1/comps/${compId}/layers`, data);
    return response.data;
  }

  async updateCompLayer(compId: string, layerId: string, data: {
    label?: string;
    key?: string;
    type?: string;
    zIndex?: number;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    properties?: string;
    transferMode?: string;
    visible?: boolean;
    opacity?: number;
    rotation?: number;
    srcImage?: string;
  }): Promise<{
    layer: {
      id: string;
      compId: string;
      label: string;
      key: string;
      type: string;
      zIndex: number;
      x: number;
      y: number;
      width?: number;
      height?: number;
      properties: string;
      transferMode: string;
      srcImage?: string;
      visible: boolean;
      opacity: number;
      rotation: number;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.patch(`/v1/comps/${compId}/layers/${layerId}`, data);
    return response.data;
  }

  async randomizeLayer(compId: string, layerId: string): Promise<{
    layer: {
      id: string;
      compId: string;
      label: string;
      key: string;
      type: string;
      zIndex: number;
      x: number;
      y: number;
      width?: number;
      height?: number;
      properties: string;
      transferMode: string;
      srcImage?: string;
      visible: boolean;
      opacity: number;
      rotation: number;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.post(`/v1/comps/${compId}/layers/${layerId}/randomize`);
    return response.data;
  }

  // ============================================================
  // Comp Template Management
  // ============================================================

  async getCompTemplates(options?: { isDefault?: boolean }): Promise<{
    templates: Array<{
      id: string;
      name: string;
      description?: string;
      width: number;
      height: number;
      backgroundColor?: string;
      isDefault: boolean;
      compData: any;
      createdAt: string;
      updatedAt: string;
    }>;
  }> {
    const params = new URLSearchParams();
    if (options?.isDefault !== undefined) {
      params.append('isDefault', options.isDefault.toString());
    }
    const response = await this.client.get(`/v1/comp-templates?${params.toString()}`);
    return response.data;
  }

  async getCompTemplate(id: string): Promise<{
    template: {
      id: string;
      name: string;
      description?: string;
      width: number;
      height: number;
      backgroundColor?: string;
      isDefault: boolean;
      compData: any;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.get(`/v1/comp-templates/${id}`);
    return response.data;
  }

  async createCompTemplate(data: {
    name: string;
    description?: string;
    width: number;
    height: number;
    backgroundColor?: string;
    compData: any;
    isDefault?: boolean;
  }): Promise<{
    template: {
      id: string;
      name: string;
      description?: string;
      width: number;
      height: number;
      backgroundColor?: string;
      isDefault: boolean;
      compData: any;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.post('/v1/comp-templates', data);
    return response.data;
  }

  async updateCompTemplate(id: string, data: {
    name?: string;
    description?: string;
    width?: number;
    height?: number;
    backgroundColor?: string;
    compData?: any;
    isDefault?: boolean;
  }): Promise<{
    template: {
      id: string;
      name: string;
      description?: string;
      width: number;
      height: number;
      backgroundColor?: string;
      isDefault: boolean;
      compData: any;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.patch(`/v1/comp-templates/${id}`, data);
    return response.data;
  }

  async deleteCompTemplate(id: string): Promise<{
    message: string;
  }> {
    const response = await this.client.delete(`/v1/comp-templates/${id}`);
    return response.data;
  }

  // ============================================================
  // Layout Management
  // ============================================================

  async getLayouts(options?: { brandId?: string }): Promise<{
    layouts: Array<{
      id: string;
      name: string;
      description?: string;
      brandId?: string;
      isDefault: boolean;
      layers: Array<{
        type: string;
        x: number;
        y: number;
        width?: number;
        height?: number;
        zIndex: number;
      }>;
      createdAt: string;
      updatedAt: string;
    }>;
  }> {
    const params = new URLSearchParams();
    if (options?.brandId) params.append('brandId', options.brandId);
    const response = await this.client.get(`/v1/layouts?${params.toString()}`);
    return response.data;
  }

  async getLayout(id: string): Promise<{
    layout: {
      id: string;
      name: string;
      description?: string;
      brandId?: string;
      isDefault: boolean;
      layers: Array<{
        type: string;
        x: number;
        y: number;
        width?: number;
        height?: number;
        zIndex: number;
      }>;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.get(`/v1/layouts/${id}`);
    return response.data;
  }

  async createLayout(data: {
    name: string;
    description?: string;
    brandId?: string;
    isDefault?: boolean;
    layers: Array<{
      type: string;
      x: number;
      y: number;
      width?: number;
      height?: number;
      zIndex: number;
    }>;
  }): Promise<{
    layout: {
      id: string;
      name: string;
      description?: string;
      brandId?: string;
      isDefault: boolean;
      layers: Array<any>;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.post('/v1/layouts', data);
    return response.data;
  }

  async updateLayout(id: string, data: {
    name?: string;
    description?: string;
    isDefault?: boolean;
    layers?: Array<{
      type: string;
      x: number;
      y: number;
      width?: number;
      height?: number;
      zIndex: number;
    }>;
  }): Promise<{
    layout: {
      id: string;
      name: string;
      description?: string;
      brandId?: string;
      isDefault: boolean;
      layers: Array<any>;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.patch(`/v1/layouts/${id}`, data);
    return response.data;
  }

  async deleteLayout(id: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/v1/layouts/${id}`);
    return response.data;
  }

  // ============================================================
  // Tone Analysis
  // ============================================================

  async analyzeAssetTone(assetId: string): Promise<{
    asset: any;
    analysis: {
      tone: string | null;
      confidence: number;
      analysis: {
        averageLightness: number;
        averageSaturation: number;
        colorCount: number;
      };
    };
  }> {
    const response = await this.client.post(`/v1/assets/${assetId}/analyze-tone`);
    return response.data;
  }

  async batchAnalyzeTone(data: {
    assetIds?: string[];
    projectId?: string;
    type?: string;
  }): Promise<{
    results: Array<{
      assetId: string;
      tone: string | null;
      success: boolean;
      error?: string;
    }>;
  }> {
    const response = await this.client.post('/v1/assets/batch-analyze-tone', data);
    return response.data;
  }

  // ============================================================
  // Version Management
  // ============================================================

  async getVersions(options?: { page?: number; limit?: number; projectId?: string; compId?: string }): Promise<{
    versions: Array<{
      id: string;
      name: string;
      description?: string;
      width: number;
      height: number;
      backgroundColor?: string;
      layers: string; // JSON string
      compId?: string;
      projectId: string;
      userId: string;
      source: string;
      renderedUrl?: string;
      renderedS3Key?: string;
      layersUrl?: string; // JSON array string
      layersS3Keys?: string; // JSON array string
      comp?: {
        id: string;
        name: string;
      };
      project?: {
        id: string;
        name: string;
        code: string;
      };
      user?: {
        id: string;
        email: string;
        name?: string;
      };
      createdAt: string;
      updatedAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.projectId) params.append('projectId', options.projectId);
    if (options?.compId) params.append('compId', options.compId);
    const response = await this.client.get(`/v1/versions?${params.toString()}`);
    return response.data;
  }

  async getVersion(id: string): Promise<{
    version: {
      id: string;
      name: string;
      description?: string;
      width: number;
      height: number;
      backgroundColor?: string;
      layers: string;
      compId?: string;
      projectId: string;
      userId: string;
      source: string;
      renderedUrl?: string;
      renderedS3Key?: string;
      layersUrl?: string;
      layersS3Keys?: string;
      comp?: {
        id: string;
        name: string;
      };
      project?: {
        id: string;
        name: string;
        code: string;
      };
      user?: {
        id: string;
        email: string;
        name?: string;
      };
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.get(`/v1/versions/${id}`);
    return response.data;
  }

  async createVersion(data: {
    name: string;
    description?: string;
    width: number;
    height: number;
    backgroundColor?: string;
    layers: string; // JSON string
    compId?: string;
    projectId: string;
    source?: 'workspace' | 'psd-plugin';
    renderedUrl?: string;
    renderedS3Key?: string;
    layersUrl?: string; // JSON array string
    layersS3Keys?: string; // JSON array string
  }): Promise<{
    version: {
      id: string;
      name: string;
      description?: string;
      width: number;
      height: number;
      backgroundColor?: string;
      layers: string;
      compId?: string;
      projectId: string;
      userId: string;
      source: string;
      renderedUrl?: string;
      renderedS3Key?: string;
      layersUrl?: string;
      layersS3Keys?: string;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.post('/v1/versions', data);
    return response.data;
  }

  async deleteVersion(id: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/v1/versions/${id}`);
    return response.data;
  }

  // ============================================================
  // Chat / Brand Discovery
  // ============================================================

  async chatBrandDiscovery(
    message: string,
    brandId?: string,
    context?: any
  ): Promise<{
    response: string;
    suggestions?: any;
    nextStep?: string;
  }> {
    const response = await this.client.post('/v1/chat/brand-discovery', {
      message,
      brandId,
      context,
    });
    return response.data;
  }

  async searchBrandInfo(data: {
    website?: string;
    instagramHandle?: string;
    brandName?: string;
  }): Promise<{
    address?: string;
    phone?: string;
    email?: string;
    instagramHandle?: string;
    socialLinks?: string[];
    websiteContent?: string;
    logoUrls?: string[];
    photoUrls?: string[];
    colorPalette?: {
      colors: Array<{
        hex?: string;
        rgb?: { r: number; g: number; b: number };
        hsl?: { h: number; s: number; l: number };
        percentage?: number;
      }>;
      dominantColor: {
        hex?: string;
        rgb?: { r: number; g: number; b: number };
        hsl?: { h: number; s: number; l: number };
        percentage?: number;
      };
    };
  }> {
    const response = await this.client.post('/v1/chat/search-brand', data);
    return response.data;
  }

  async uploadBrandPhotos(data: {
    photoUrls: string[];
    brandId: string;
    galleryId?: string;
  }): Promise<{
    uploaded: number;
    failed: number;
    total: number;
    errors?: string[];
    message: string;
  }> {
    const response = await this.client.post('/v1/chat/upload-brand-photos', data);
    return response.data;
  }

  async getGalleries(params?: {
    brandId?: string;
  }): Promise<{
    galleries: Array<{
      id: string;
      name: string;
      description?: string;
      ownerBrandId?: string;
      isPrivate: boolean;
      isDefault: boolean;
      permissions: any[];
      accessLevel?: string;
      createdAt: string;
      updatedAt: string;
    }>;
  }> {
    const response = await this.client.get('/v1/galleries', { params });
    return response.data;
  }

  async analyzeBrandContent(data: {
    websiteUrl: string;
    brandName?: string;
    instagramHandle?: string;
    websiteMetadata?: {
      title?: string;
      description?: string;
      keywords?: string[];
    };
    customQuestions?: string[];
  }): Promise<{
    brandCode: string;
    elevatorPitch: string;
    voiceTone: string;
    taglines: string[];
    positiveKeywords: string[];
    negativeKeywords: string[];
    colorPalette?: {
      colors: Array<{
        hex?: string;
        rgb?: { r: number; g: number; b: number };
        hsl?: { h: number; s: number; l: number };
        percentage?: number;
      }>;
      dominantColor: {
        hex?: string;
        rgb?: { r: number; g: number; b: number };
        hsl?: { h: number; s: number; l: number };
        percentage?: number;
      };
    };
    logoUrls?: string[];
    customAnswers?: Record<string, string>;
  }> {
    const response = await this.client.post('/v1/chat/analyze-brand', data);
    return response.data;
  }

  async askBrandingQuestion(data: {
    websiteUrl: string;
    question: string;
    websiteMetadata?: {
      title?: string;
      description?: string;
      keywords?: string[];
    };
  }): Promise<{
    answer: string;
    confidence?: number;
  }> {
    const response = await this.client.post('/v1/chat/ask-branding-question', data);
    return response.data;
  }

  async updateBrandFromChat(
    brandId: string,
    updates: any
  ): Promise<{
    brand: {
      id: string;
      name: string;
      code: string;
      description?: string;
      contactName?: string;
      contactEmail?: string;
      phone?: string;
      address?: string;
      website?: string;
      igHandle?: string;
      fbHandle?: string;
      logoUrls?: string;
      colors?: string;
      typefaces?: string;
      usp?: string;
      notes?: string;
      voiceTone?: string;
      taglines?: string;
      elevatorPitch?: string;
      positiveBrandKeywords?: string;
      negativeBrandKeywords?: string;
      teamMembers?: string;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    const response = await this.client.post('/v1/chat/update-brand', {
      brandId,
      updates,
    });
    return response.data;
  }

  // Gallery management
  async fetchGalleries(brandId?: string): Promise<{
    galleries: Array<{
      id: string;
      name: string;
      description?: string;
      ownerBrandId?: string;
      isPrivate: boolean;
      isDefault: boolean;
      permissions: any[];
      accessLevel: 'READ' | 'WRITE' | 'ADMIN';
      imageCount?: number;
      createdAt: string;
      updatedAt: string;
    }>;
  }> {
    const params = new URLSearchParams();
    if (brandId) params.append('brandId', brandId);
    const response = await this.client.get(`/v1/galleries?${params.toString()}`);
    return response.data;
  }

  async fetchGallery(galleryId: string, brandId?: string): Promise<{
    id: string;
    name: string;
    description?: string;
    ownerBrandId?: string;
    isPrivate: boolean;
    isDefault: boolean;
    permissions: any[];
    accessLevel: 'READ' | 'WRITE' | 'ADMIN';
    imageCount: number;
    createdAt: string;
    updatedAt: string;
  }> {
    const params = new URLSearchParams();
    if (brandId) params.append('brandId', brandId);
    const response = await this.client.get(`/v1/galleries/${galleryId}?${params.toString()}`);
    return response.data;
  }

  async createGallery(data: {
    name: string;
    description?: string;
    ownerBrandId?: string;
    isPrivate?: boolean;
    permissions?: any[];
    galleryType?: 'images' | 'type-library';
  }): Promise<{
    id: string;
    name: string;
    description?: string;
    ownerBrandId?: string;
    isPrivate: boolean;
    isDefault: boolean;
    permissions: any[];
    createdAt: string;
    updatedAt: string;
  }> {
    const activeBrandId = this.getActiveBrandId();
    const payload = {
      ...data,
      ownerBrandId: data.ownerBrandId || activeBrandId,
      isPrivate: data.isPrivate ?? true,
    };
    const response = await this.client.post('/v1/galleries', payload);
    return response.data;
  }

  async updateGallery(
    galleryId: string,
    data: {
      name?: string;
      description?: string;
      isPrivate?: boolean;
      permissions?: any[];
    }
  ): Promise<{
    id: string;
    name: string;
    description?: string;
    ownerBrandId?: string;
    isPrivate: boolean;
    isDefault: boolean;
    permissions: any[];
    createdAt: string;
    updatedAt: string;
  }> {
    const response = await this.client.put(`/v1/galleries/${galleryId}`, data);
    return response.data;
  }

  async deleteGallery(galleryId: string): Promise<{ success: boolean }> {
    const response = await this.client.delete(`/v1/galleries/${galleryId}`);
    return response.data;
  }

  async fetchGalleryImages(
    galleryId: string,
    options?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      search?: string;
    }
  ): Promise<{
    images: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options?.search) params.append('search', options.search);
    const response = await this.client.get(`/v1/galleries/${galleryId}/images?${params.toString()}`);
    return response.data;
  }

  async addImagesToGallery(
    galleryId: string,
    imageIds: string[]
  ): Promise<{ success: boolean; added: number }> {
    const response = await this.client.post(`/v1/galleries/${galleryId}/images`, {
      imageIds,
    });
    return response.data;
  }

  async removeImageFromGallery(
    galleryId: string,
    imageId: string
  ): Promise<{ success: boolean }> {
    const response = await this.client.delete(`/v1/galleries/${galleryId}/images/${imageId}`);
    return response.data;
  }

  async updateGalleryPermissions(
    galleryId: string,
    permissions: any[]
  ): Promise<{
    id: string;
    name: string;
    description?: string;
    ownerBrandId?: string;
    isPrivate: boolean;
    isDefault: boolean;
    permissions: any[];
    createdAt: string;
    updatedAt: string;
  }> {
    const response = await this.client.put(`/v1/galleries/${galleryId}/permissions`, {
      permissions,
    });
    return response.data;
  }

  /**
   * Export gallery: returns JSON metadata (metadataOnly), ZIP blob (below threshold), or 202 with jobId (above threshold).
   */
  async exportGallery(
    galleryId: string,
    options?: { metadataOnly?: boolean }
  ): Promise<
    | { type: 'metadata'; data: Record<string, unknown> }
    | { type: 'blob'; data: Blob; filename: string }
    | { type: 'job'; jobId: string }
  > {
    const params = new URLSearchParams();
    if (options?.metadataOnly) params.append('metadata', '1');
    const url = `/v1/galleries/${galleryId}/export${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await this.client.get(url, {
      responseType: options?.metadataOnly ? 'json' : 'blob',
      validateStatus: (s) => s === 200 || s === 202,
    });
    if (response.status === 202) {
      const raw = response.data;
      const body =
        typeof raw === 'string'
          ? JSON.parse(raw)
          : raw instanceof Blob
            ? JSON.parse(await raw.text())
            : raw;
      return { type: 'job', jobId: body.jobId };
    }
    if (options?.metadataOnly) {
      return { type: 'metadata', data: response.data as Record<string, unknown> };
    }
    const blob = response.data as Blob;
    const disposition = response.headers['content-disposition'];
    const filenameMatch = disposition?.match(/filename="?([^";]+)"?/);
    const filename = filenameMatch ? filenameMatch[1] : 'gallery_export.zip';
    return { type: 'blob', data: blob, filename };
  }

  /**
   * Poll export job status. When completed, returns downloadLinks.
   */
  async getExportJobStatus(jobId: string): Promise<{
    status: 'processing' | 'completed' | 'failed';
    downloadLinks?: Array<{ url: string; filename: string }>;
    error?: string;
  }> {
    const response = await this.client.get(`/v1/export/jobs/${jobId}`);
    return response.data;
  }

  /**
   * Download an export part (authenticated). Returns blob for the UI to trigger save.
   */
  async downloadExportPart(relativeUrl: string): Promise<Blob> {
    const response = await this.client.get(relativeUrl, { responseType: 'blob' });
    return response.data as Blob;
  }

  async generateImage(params: {
    assetType: 'bg' | 'photo' | 'sticker';
    prompt: string;
    imageIds: string[];
    aspectRatio: string;
    imageSize?: '1K' | '2K' | '4K';
    projectId: string;
    name: string;
  }): Promise<any> {
    const response = await this.client.post('/v1/generate/image', params);
    return response.data;
  }

  // --- Company research ---

  async getCompanies(options?: { page?: number; limit?: number }): Promise<{
    companies: Array<{
      id: string;
      name: string;
      websiteUrl: string;
      instagramHandle: string;
      relationType: string | null;
      researchInProgress: boolean;
      lastResearchAt: string | null;
      lastResearchError: string | null;
      createdAt: string;
      updatedAt: string;
    }>;
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', String(options.page));
    if (options?.limit) params.append('limit', String(options.limit));
    const response = await this.client.get(`/v1/companies?${params.toString()}`);
    return response.data;
  }

  async getCompany(
    id: string,
    options?: {
      includePosts?: boolean;
      includeComments?: boolean;
      postsLimit?: number;
      reconcileResearch?: boolean;
    },
  ): Promise<{
    company: Record<string, unknown>;
    posts?: Array<Record<string, unknown>>;
  }> {
    const params = new URLSearchParams();
    if (options?.includePosts === false) params.append('includePosts', '0');
    if (options?.includeComments === false) params.append('includeComments', '0');
    if (options?.postsLimit) params.append('postsLimit', String(options.postsLimit));
    if (options?.reconcileResearch === false) params.append('reconcileResearch', '0');
    const q = params.toString();
    const response = await this.client.get(`/v1/companies/${id}${q ? `?${q}` : ''}`);
    return response.data;
  }

  async createCompany(data: {
    name: string;
    websiteUrl: string;
    instagramHandle: string;
    relationType?: 'client' | 'prospect' | 'competitor' | 'reference';
    notes?: string;
    linkedBrandId?: string;
  }): Promise<{ company: Record<string, unknown> }> {
    const response = await this.client.post('/v1/companies', data);
    return response.data;
  }

  async runCompanyResearch(
    id: string,
    body?: {
      depth?: 'light' | 'default' | 'heavy';
      skipWebSearch?: boolean;
      skipWebsite?: boolean;
      skipInstagramProfile?: boolean;
      skipInstagramPosts?: boolean;
      skipInstagramComments?: boolean;
    },
  ): Promise<{
    ok: boolean;
    message: string;
    companyId: string;
    sessionId: string;
    depth: string;
  }> {
    const response = await this.client.post(`/v1/companies/${id}/run-research`, body ?? {});
    return response.data;
  }

  async cancelCompanyResearch(id: string): Promise<{
    ok: boolean;
    companyId: string;
    cancelledRuns: number;
    sessionId: string | null;
  }> {
    const response = await this.client.post(`/v1/companies/${id}/cancel-research`);
    return response.data;
  }

  async getResearchSessions(companyId: string): Promise<{
    sessions: Array<{
      id: string;
      startedAt: string;
      finishedAt: string | null;
      depth: string | null;
    }>;
  }> {
    const response = await this.client.get(`/v1/companies/${companyId}/research-sessions`);
    return response.data;
  }

  async getResearchSession(
    companyId: string,
    sessionId: string,
  ): Promise<{
    session: {
      id: string;
      companyId: string;
      startedAt: string;
      finishedAt: string | null;
      depth: string | null;
      options: unknown;
      export: unknown;
    };
    runs: Array<{
      id: string;
      source: string;
      status: string;
      depth: string | null;
      apifyRunId: string | null;
      error: string | null;
      createdAt: string;
    }>;
  }> {
    const response = await this.client.get(
      `/v1/companies/${companyId}/research-sessions/${sessionId}`,
    );
    return response.data;
  }

  async cacheCompanyMedia(companyId: string): Promise<{ ok: boolean; message: string }> {
    const response = await this.client.post(`/v1/companies/${companyId}/cache-media`);
    return response.data;
  }

  async deleteCompany(id: string): Promise<void> {
    await this.client.delete(`/v1/companies/${id}`);
  }
}

export const apiClient = new ApiClient()
